import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import { dashboardApi, authApi, oesApi, postureRequestApi, reportsApi, integrationsApi, settingsApi } from '../services/api';
import { useInstanceMode } from '../providers/InstanceModeProvider';
import { getCriticalityLabels } from '../utils/settingsScales';
import { useLabels } from '../providers/LabelsProvider';
import { useT } from '../providers/I18nProvider';
import RadarChart from '../components/RadarChart';
import Layout from '../components/layout/Layout';
import AssessmentModal from '../components/AssessmentModal';
import AdminCriticalityDashboard from '../components/AdminCriticalityDashboard';
import AdminPostureDashboard from '../components/AdminPostureDashboard';
import AdminRiskDashboard from '../components/AdminRiskDashboard';
import Card from '../components/common/Card';
import AnimatedCounter from '../components/AnimatedCounter';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart as RechartsBar,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const CRITICALITY_COLORS = { MA: '#dc2626', A: '#f97316', M: '#eab308', B: '#3b82f6', MB: '#22c55e' };
const CRITICALITY_LABELS = { MA: 'Very High', A: 'High', M: 'Moderate', B: 'Low', MB: 'Very Low' };
const PIE_COLORS = ['#3b82f6', '#6366f1', '#22c55e', '#f97316', '#dc2626', '#eab308', '#8b5cf6', '#14b8a6', '#ec4899', '#64748b'];

function SummaryStatCard({ label, value, sub, color = 'text-eucnet-navy', icon }) {
  return (
    <Card className="p-5 flex flex-col items-center text-center">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{label}</p>
      <div className="flex items-center gap-2.5">
        {icon && <div className="p-2 rounded-lg bg-slate-50 text-slate-400 flex-shrink-0 [&>svg]:w-6 [&>svg]:h-6">{icon}</div>}
        <p className={`text-3xl font-bold ${color} leading-tight`}>{value}</p>
      </div>
      {sub && <p className="text-[10px] text-slate-400 mt-1">{sub}</p>}
    </Card>
  );
}

function PlatformSummary({ report, stats }) {
  const labels = useLabels();
  const t = useT();
  const statusData = useMemo(() => {
    const statusLabels = { draft: t('common.draft'), active: t('common.active'), paused: t('common.paused'), completed: t('common.completed'), archived: t('common.archived') };
    const colors = ['#94a3b8', '#3b82f6', '#eab308', '#22c55e', '#64748b'];
    return Object.entries(report.assessment_status_counts || {})
      .filter(([, v]) => v > 0)
      .map(([k, v], i) => ({ name: statusLabels[k] || k, value: v, color: colors[i % colors.length] }));
  }, [report, t]);

  const sectorData = useMemo(() =>
    (report.sector_distribution || []).map((s, i) => ({ name: s.sector, value: s.count, color: PIE_COLORS[i % PIE_COLORS.length] })),
    [report]
  );

  const critData = useMemo(() =>
    (report.criticality_distribution || []).map(d => ({
      name: CRITICALITY_LABELS[d.level] || d.level,
      value: d.count,
      color: CRITICALITY_COLORS[d.level] || '#94a3b8',
    })),
    [report]
  );

  const criticalRisks = stats?.risk_stats?.critical_risks || 0;
  const highRisks = stats?.risk_stats?.high_risks || 0;
  const openVulns = stats?.vulnerability_stats?.open_vulnerabilities || 0;

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{t('dashboard.platformSummary')}</h2>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <SummaryStatCard
          label={t('nav.assessments')}
          value={<AnimatedCounter value={report.total_assessments || 0} duration={1200} />}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
        />
        <SummaryStatCard
          label={t('dashboard.entityProfiled', { entity: labels.entityName })}
          value={<AnimatedCounter value={report.total_oes || 0} duration={1200} />}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
        />
        <SummaryStatCard
          label={t('dashboard.avgCompliance')}
          value={`${report.average_compliance || 0}%`}
          color={report.average_compliance >= 60 ? 'text-green-600' : 'text-orange-500'}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <SummaryStatCard
          label={t('dashboard.criticalRisks')}
          value={<AnimatedCounter value={criticalRisks} duration={1200} />}
          color={criticalRisks > 0 ? 'text-red-600' : 'text-slate-700'}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
        />
        <SummaryStatCard
          label={t('dashboard.highRisks')}
          value={<AnimatedCounter value={highRisks} duration={1200} />}
          color={highRisks > 0 ? 'text-orange-500' : 'text-slate-700'}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
        />
        <SummaryStatCard
          label={t('dashboard.openVulns')}
          value={<AnimatedCounter value={openVulns} duration={1200} />}
          color={openVulns > 0 ? 'text-amber-600' : 'text-slate-700'}
          icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" /></svg>}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('dashboard.assessmentStatus')}</h4>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={190}>
              <RechartsPie>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={68}
                  paddingAngle={3}
                  strokeWidth={0}
                  label={({ name, value, cx, x, y }) => (
                    <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fill="#64748b" fontSize={10} fontWeight={500}>
                      {`${name}: ${value}`}
                    </text>
                  )}
                >
                  {statusData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11 }} />
              </RechartsPie>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">No data</p>}
        </Card>

        <Card className="p-4">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('dashboard.sectorDistribution')}</h4>
          {sectorData.length > 0 ? (
            <ResponsiveContainer width="100%" height={190}>
              <RechartsPie>
                <Pie
                  data={sectorData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={42}
                  outerRadius={68}
                  paddingAngle={3}
                  strokeWidth={0}
                  label={({ name, value, cx, x, y }) => (
                    <text x={x} y={y} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fill="#64748b" fontSize={10} fontWeight={500}>
                      {`${name}: ${value}`}
                    </text>
                  )}
                >
                  {sectorData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11 }} />
              </RechartsPie>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">No data</p>}
        </Card>

        <Card className="p-4">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('dashboard.criticalityDistribution')}</h4>
          {critData.length > 0 ? (
            <ResponsiveContainer width="100%" height={190}>
              <RechartsBar data={critData} layout="vertical" barSize={12}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 11 }} />
                <Bar dataKey="value" name={`${labels.entityName} Count`} radius={[0, 6, 6, 0]}>
                  {critData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </RechartsBar>
            </ResponsiveContainer>
          ) : <p className="text-xs text-slate-400 text-center py-8">No data</p>}
        </Card>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const labels = useLabels();
  const t = useT();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [reportSummary, setReportSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [completedAssessmentInfo, setCompletedAssessmentInfo] = useState(null);
  const [postureRequestStatus, setPostureRequestStatus] = useState(null);
  const [questionnaireSubmitted, setQuestionnaireSubmitted] = useState(false);
  const [criticalityDashboardExpanded, setCriticalityDashboardExpanded] = useState(true);
  const [postureDashboardExpanded, setPostureDashboardExpanded] = useState(true);
  const [riskDashboardExpanded, setRiskDashboardExpanded] = useState(true);

  const { pureOES } = useInstanceMode();
  const [myOES, setMyOES] = useState(null);
  const [myAssessment, setMyAssessment] = useState(null);
  const [myIntegrationSummary, setMyIntegrationSummary] = useState(null);
  const [myPosture, setMyPosture] = useState(null);
  const [myScales, setMyScales] = useState(null);

  const role = localStorage.getItem('role');
  const isRegularUser = role === 'user';
  const isAdmin = role === 'global_admin' || role === 'assessment_admin' || role === 'admin';
  const isAssessmentAdmin = role === 'assessment_admin';
  const isOrgAdmin = role === 'admin';
  const isGlobalAdmin = role === 'global_admin';
  const showOESManagementCard = isGlobalAdmin || isOrgAdmin;
  const showAssessmentResultsCard = isAssessmentAdmin || isOrgAdmin || isGlobalAdmin;
  const showAssessmentManagementCard = isAssessmentAdmin || isOrgAdmin || isGlobalAdmin;
  const showOrganizationManagementCard = isGlobalAdmin;
  const showAdminDashboardCard = isAssessmentAdmin || isOrgAdmin || isGlobalAdmin;

  useEffect(() => {
    loadDashboard();
    if (isRegularUser) {
      loadCompletedAssessmentInfo();
    }
  }, [isRegularUser]);

  const loadDashboard = async () => {
    try {
      const [dashRes, reportRes] = await Promise.all([
        dashboardApi.getSummary(),
        isAdmin ? reportsApi.getSummary() : Promise.resolve(null),
      ]);
      setStats(dashRes.data);
      if (reportRes?.data) setReportSummary(reportRes.data);

      // OES mode: load single OES data
      if (pureOES && isAdmin) {
        try {
          const oesRes = await oesApi.getAll();
          const oesList = oesRes.data || [];
          if (oesList.length > 0) {
            const oes = oesList[0];
            setMyOES(oes);
            // Load scales for criticality labels
            try {
              const scalesRes = await settingsApi.getForCurrentUser();
              setMyScales(scalesRes.data?.scales ?? {});
            } catch { /* ignore */ }
            // Load criticality assessment
            try {
              const assessRes = await oesApi.getCurrentAssessment(oes.id);
              if (assessRes.data) setMyAssessment(assessRes.data);
            } catch { /* no assessment yet */ }
            // Load posture data
            try {
              const postureRes = await oesApi.getQuestionnaireResult(oes.id);
              if (postureRes.data) setMyPosture(postureRes.data);
            } catch { /* no posture yet */ }
            // Load integration summary
            try {
              const intRes = await integrationsApi.getOESSummary(oes.id);
              if (intRes.data) setMyIntegrationSummary(intRes.data);
            } catch { /* no integration data */ }
          }
        } catch { /* ignore */ }
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCompletedAssessmentInfo = async () => {
    try {
      const response = await authApi.getAssessmentInfo();
      const data = response.data;
      if (!data?.has_assessment || !data?.oes_id) {
        setShowAssessmentModal(false);
        setPostureRequestStatus(null);
        return;
      }
      const assessmentRes = await oesApi.getCurrentAssessment(data.oes_id);
      const assessment = assessmentRes.data;
      if (assessment?.oes_criticality) {
        const assessmentId = data.assessment?.id;
        setCompletedAssessmentInfo({
          oes_id: data.oes_id,
          oes: data.oes,
          assessment_id: assessmentId,
        });
        setShowAssessmentModal(false);
        if (assessmentId) {
          try {
            const statusRes = await postureRequestApi.getStatus(data.oes_id, assessmentId);
            setPostureRequestStatus(statusRes.data?.requested ? statusRes.data : null);
            
            // Check if questionnaire has been submitted
            try {
              const questionnaireResultRes = await oesApi.getQuestionnaireResult(data.oes_id);
              setQuestionnaireSubmitted(!!questionnaireResultRes.data?.submitted_at);
            } catch {
              // No questionnaire result yet - not submitted
              setQuestionnaireSubmitted(false);
            }
          } catch {
            setPostureRequestStatus(null);
            setQuestionnaireSubmitted(false);
          }
        } else {
          setPostureRequestStatus(null);
          setQuestionnaireSubmitted(false);
        }
      } else {
        setCompletedAssessmentInfo(null);
        setPostureRequestStatus(null);
        setShowAssessmentModal(true);
      }
    } catch (err) {
      setCompletedAssessmentInfo(null);
      setPostureRequestStatus(null);
      // 404 = no OES criticality submission yet → show Welcome modal to start assessment
      setShowAssessmentModal(err.response?.status === 404);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-brand-500 mx-auto" />
          <p className="mt-4 text-slate-600 text-sm font-medium">{t('dashboard.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      {isRegularUser && (
        <AssessmentModal
          isOpen={showAssessmentModal}
          onClose={() => setShowAssessmentModal(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 p-6 space-y-6">
        {/* ── OES Dashboard (OES mode, admin) ── */}
        {pureOES && isAdmin && myOES && (() => {
          const critLabels = getCriticalityLabels(myScales);
          const critKey = (myAssessment?.oes_criticality || '').toLowerCase();
          const critLabel = critLabels[critKey] || myAssessment?.oes_criticality || '--';
          const CRIT_COLORS = { ma: 'text-red-600', a: 'text-orange-500', m: 'text-amber-600', b: 'text-blue-600', mb: 'text-emerald-600' };
          const critColor = CRIT_COLORS[critKey] || 'text-slate-300';
          const services = myAssessment?.services || [];
          const processes = myAssessment?.processes || [];
          // Normalize by_domain: values may be objects {total, assessed, score_sum, pct} or plain numbers
          const rawByDomain = myPosture?.by_domain || {};
          const postureByDomain = {};
          Object.entries(rawByDomain).forEach(([k, v]) => {
            postureByDomain[k] = typeof v === 'object' && v !== null ? (v.pct ?? 0) : (v ?? 0);
          });
          const overallCompliance = myPosture?.overall_compliance;

          return (
          <div className="space-y-4">
            {/* OES Identity */}
            <div className="flex items-center gap-4">
              {myOES.logo && (
                <img src={myOES.logo} alt="" className="w-12 h-12 rounded-lg object-contain bg-slate-50 border border-slate-200" onError={e => { e.target.style.display = 'none'; }} />
              )}
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{myOES.sector || `${labels.entityName} Dashboard`}</p>
                <h1 className="text-xl font-bold text-eucnet-navy">{myOES.name}</h1>
              </div>
            </div>

            {/* Score Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                <p className="text-[10px] font-semibold text-slate-400 uppercase">{t('dashboard.criticality')}</p>
                <p className={`text-xl font-bold mt-1 ${critColor}`}>{critLabel}</p>
                {myAssessment?.impact_average && (
                  <p className="text-[10px] text-slate-500 mt-0.5">Impact: {myAssessment.impact_average.toFixed(1)}</p>
                )}
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                <p className="text-[10px] font-semibold text-slate-400 uppercase">{t('dashboard.compliance')}</p>
                <p className="text-xl font-bold mt-1 text-eucnet-sky">
                  {overallCompliance != null ? `${Math.round(overallCompliance)}%` : (reportSummary?.average_compliance != null ? `${Math.round(reportSummary.average_compliance)}%` : '--')}
                </p>
                {myPosture?.framework && <p className="text-[10px] text-slate-500 mt-0.5">{myPosture.framework}</p>}
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                <p className="text-[10px] font-semibold text-slate-400 uppercase">{t('dashboard.riskLevel')}</p>
                <p className={`text-xl font-bold mt-1 ${
                  reportSummary?.max_risk_score >= 4 ? 'text-red-600' :
                  reportSummary?.max_risk_score >= 3 ? 'text-orange-500' :
                  reportSummary?.max_risk_score >= 2 ? 'text-amber-500' : 'text-emerald-500'
                }`}>
                  {reportSummary?.max_risk_score != null
                    ? (reportSummary.max_risk_score >= 4 ? t('risk.critical') : reportSummary.max_risk_score >= 3 ? t('risk.high') : reportSummary.max_risk_score >= 2 ? t('risk.moderate') : t('risk.low'))
                    : '--'}
                </p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                <p className="text-[10px] font-semibold text-slate-400 uppercase">{t('dashboard.vulnerabilities')}</p>
                <p className={`text-xl font-bold mt-1 ${
                  (myIntegrationSummary?.critical || 0) + (myIntegrationSummary?.high || 0) > 0 ? 'text-red-600' : 'text-slate-300'
                }`}>
                  {myIntegrationSummary?.total != null ? myIntegrationSummary.total : '--'}
                </p>
                {myIntegrationSummary?.total > 0 && (
                  <p className="text-[10px] text-slate-500 mt-0.5">{myIntegrationSummary.critical} crit / {myIntegrationSummary.high} high</p>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3">
              <button onClick={() => navigate('/admin/assessments')} className="px-4 py-2 text-sm font-medium bg-eucnet-sky text-white rounded-lg hover:bg-eucnet-navy transition-colors">
                {t('dashboard.manageAssessments')}
              </button>
              <button onClick={() => navigate('/admin/assessment-results')} className="px-4 py-2 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                {t('dashboard.viewResults')}
              </button>
              <button onClick={() => navigate('/admin/settings')} className="px-4 py-2 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                {t('dashboard.integrations')}
              </button>
              <button onClick={() => navigate('/admin/reports')} className="px-4 py-2 text-sm font-medium border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                {t('dashboard.reports')}
              </button>
            </div>

            {/* Services & Processes Detail */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Services */}
              {services.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">{t('dashboard.essentialServices')}</h3>
                  <div className="space-y-2">
                    {services.map((s, i) => {
                      const avgScore = ((s.user_count_score || 1) + (s.frequency_score || 1) + (6 - (s.replaceability_score || 1)) + (s.interdependency_score || 1) + (6 - (s.time_to_impact_score || 1))) / 5;
                      return (
                        <div key={i} className="flex items-center justify-between gap-2">
                          <span className="text-sm text-slate-700 truncate">{s.service_name || `Service ${i + 1}`}</span>
                          <div className="flex items-center gap-1.5">
                            <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-eucnet-sky" style={{ width: `${(avgScore / 5) * 100}%` }} />
                            </div>
                            <span className="text-[10px] font-bold text-slate-500 w-6 text-right">{avgScore.toFixed(1)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Processes & IT/OT */}
              {processes.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-lg p-5">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">{t('dashboard.criticalProcesses')}</h3>
                  <div className="space-y-2">
                    {processes.map((p, i) => {
                      const systems = typeof p.it_ot_systems === 'string' ? p.it_ot_systems.split(',').map(s => s.trim()).filter(Boolean) : (p.it_ot_systems || []);
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-slate-700 truncate">{p.process_name || `Process ${i + 1}`}</span>
                            <span className="text-[10px] font-medium text-slate-500">Automation: {p.automation_level || 0}/5</span>
                          </div>
                          {systems.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {systems.slice(0, 5).map(s => (
                                <span key={s} className="px-1.5 py-0.5 text-[9px] bg-slate-100 text-slate-600 rounded">{s}</span>
                              ))}
                              {systems.length > 5 && <span className="text-[9px] text-slate-400">+{systems.length - 5}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Posture Radar */}
            {Object.keys(postureByDomain).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-5">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">{t('dashboard.postureByDomain')}</h3>
                <div className="flex items-center gap-6">
                  <div className="w-64 h-64">
                    <RadarChart data={postureByDomain} />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {Object.entries(postureByDomain).map(([domain, score]) => (
                      <div key={domain} className="flex items-center gap-2">
                        <span className="text-xs text-slate-600 w-24 truncate">{domain}</span>
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${score}%`, backgroundColor: score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444' }}
                          />
                        </div>
                        <span className="text-[10px] font-bold text-slate-500 w-8 text-right">{Math.round(score)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          );
        })()}

        {/* ── Platform Summary (coordinator admin only) ── */}
        {isAdmin && !pureOES && reportSummary && <PlatformSummary report={reportSummary} stats={stats} />}

        {isAdmin && !pureOES && (
          <>
            {/* Criticality Dashboard Section — coordinator only */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setCriticalityDashboardExpanded(!criticalityDashboardExpanded)}
                className="w-full flex items-center justify-between px-6 py-2 bg-slate-50 hover:bg-slate-100 text-left"
              >
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{t('dashboard.criticalityDashboard')}</h2>
                <span className="text-slate-500 text-lg">{criticalityDashboardExpanded ? '▼' : '▶'}</span>
              </button>
              {criticalityDashboardExpanded && (
                <div className="p-6">
                  <AdminCriticalityDashboard />
                </div>
              )}
            </div>

            {/* Posture Dashboard Section */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setPostureDashboardExpanded(!postureDashboardExpanded)}
                className="w-full flex items-center justify-between px-6 py-2 bg-slate-50 hover:bg-slate-100 text-left"
              >
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{t('dashboard.postureDashboard')}</h2>
                <span className="text-slate-500 text-lg">{postureDashboardExpanded ? '▼' : '▶'}</span>
              </button>
              {postureDashboardExpanded && (
                <div className="p-6">
                  <AdminPostureDashboard />
                </div>
              )}
            </div>

            {/* Risk Dashboard Section */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setRiskDashboardExpanded(!riskDashboardExpanded)}
                className="w-full flex items-center justify-between px-6 py-2 bg-slate-50 hover:bg-slate-100 text-left"
              >
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{t('dashboard.riskDashboard')}</h2>
                <span className="text-slate-500 text-lg">{riskDashboardExpanded ? '▼' : '▶'}</span>
              </button>
              {riskDashboardExpanded && (
                <div className="p-6">
                  <AdminRiskDashboard />
                </div>
              )}
            </div>
          </>
        )}

        {isRegularUser && postureRequestStatus?.requested && !questionnaireSubmitted && (
          <Card className="border-l-4 border-l-amber-500 bg-amber-50/50">
            <h3 className="text-lg font-medium text-slate-900 mb-2">Posture questionnaire requested</h3>
            <p className="text-slate-600 text-sm mb-4">
              Your administrator has requested that you complete the posture (gap) questionnaire for your organization.
              {postureRequestStatus.requested_by_name && (
                <span className="block mt-1 text-slate-500">Requested by {postureRequestStatus.requested_by_name}</span>
              )}
            </p>
            <button
              onClick={() => navigate(`/oes/${completedAssessmentInfo?.oes_id}/questionnaire`)}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg font-medium text-sm"
            >
              Start posture questionnaire
            </button>
          </Card>
        )}

        {isRegularUser && questionnaireSubmitted && completedAssessmentInfo && (
          <Card className="border-l-4 border-l-brand-500">
            <h3 className="text-lg font-medium text-slate-900 mb-2">Thank you for completing the posture questionnaire, {user?.user_info?.first_name || ''}!</h3>
            <p className="text-slate-600 text-sm mb-4">
              Your posture questionnaire has been submitted. You can update your answers at any time by opening the questionnaire below.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate(`/oes/${completedAssessmentInfo.oes_id}/questionnaire`)}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-500 text-white rounded-lg font-medium text-sm"
              >
                Update questionnaire
              </button>
              <button
                onClick={() => navigate(`/oes/${completedAssessmentInfo.oes_id}/questionnaire-result`)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm"
              >
                View results
              </button>
            </div>
          </Card>
        )}

        {isRegularUser && completedAssessmentInfo && (
          <Card className="border-l-4 border-l-brand-500">
            <h3 className="text-lg font-medium text-slate-900 mb-2">Thank you for completing the assessment, {user?.user_info?.first_name || ''}!</h3>
            <p className="text-slate-600 text-sm mb-4">
              Your criticality assessment has been submitted. You can update your answers at any time by opening the assessment below.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate(`/oes/${completedAssessmentInfo.oes_id}/assessment`, { state: { loadExisting: true } })}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-500 text-white rounded-lg font-medium text-sm"
              >
                Update assessment
              </button>
              <button
                onClick={() => navigate(`/oes/${completedAssessmentInfo.oes_id}/assessment`)}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm"
              >
                Reassess (new submission)
              </button>
            </div>
          </Card>
        )}

        {isAdmin && !pureOES && (() => {
          const navCards = [
            { title: 'Assessment Results', description: 'View criticality, posture and risk assessment results', path: '/admin/assessment-results', visible: showAssessmentResultsCard, borderColor: 'border-l-eucnet-sky', bgTint: 'bg-eucnet-sky-tint', hoverBg: 'hover:bg-eucnet-sky/10', iconColor: 'text-eucnet-sky', icon: (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
            )},
            { title: `${labels.entityName} Management`, description: `Manage ${labels.entityNamePluralFull}`, path: '/oes', visible: showOESManagementCard, borderColor: 'border-l-eucnet-blue', bgTint: 'bg-eucnet-blue-tint', hoverBg: 'hover:bg-eucnet-blue/10', iconColor: 'text-eucnet-blue', icon: (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
            )},
            { title: 'Assessment Management', description: 'Create and manage assessment projects', path: '/admin/assessments', visible: showAssessmentManagementCard, borderColor: 'border-l-eucnet-primary', bgTint: 'bg-eucnet-primary-tint', hoverBg: 'hover:bg-eucnet-primary/10', iconColor: 'text-eucnet-primary', icon: (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            )},
            { title: 'Organization Management', description: 'Manage organizations and their settings', path: '/admin/organizations', visible: showOrganizationManagementCard, borderColor: 'border-l-eucnet-navy', bgTint: 'bg-eucnet-navy-tint', hoverBg: 'hover:bg-eucnet-navy/10', iconColor: 'text-eucnet-navy', icon: (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
            )},
            { title: 'Admin Dashboard', description: 'Manage users and system settings', path: '/admin', visible: showAdminDashboardCard, borderColor: 'border-l-eucnet-primary', bgTint: 'bg-eucnet-primary-tint', hoverBg: 'hover:bg-eucnet-primary/10', iconColor: 'text-eucnet-primary', icon: (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
            )},
            { title: 'Security', description: 'Password policy, lockout, and 2FA requirements', path: '/admin/security', visible: showAdminDashboardCard, borderColor: 'border-l-amber-500', bgTint: 'bg-amber-50/50', hoverBg: 'hover:bg-amber-500/10', iconColor: 'text-amber-600', icon: (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            )},
          ];
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {navCards.filter(c => c.visible).map((card, index) => (
                <button
                  key={index}
                  onClick={() => navigate(card.path)}
                  className={`bg-white border border-slate-200 border-l-4 ${card.borderColor} rounded-lg shadow-sm p-5 transition-all duration-200 hover:scale-[1.02] hover:shadow-md text-left`}
                >
                  <div className="flex flex-col items-start">
                    <div className={`mb-3 p-2.5 rounded-lg bg-slate-50 ${card.iconColor}`}>
                      {card.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-eucnet-grey-800 mb-1">{card.title}</h3>
                    <p className="text-sm text-eucnet-grey-600">{card.description}</p>
                  </div>
                </button>
              ))}
            </div>
          );
        })()}
      </div>
    </Layout>
  );
}
